import { Form, FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { VehiclePointType } from "constant";
import features from "features";
import { RootStateInterface } from "reducer";
import { MissionsManagerStateInterface } from "scenes/MissionsManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import FormField from "components/inputs/FormField/FormField";

import CloseIcon from "assets/icons/close-small.svg";
import RoutewayFinishIcon from "assets/icons/routeway-finish.svg";
import RoutewayStartIcon from "assets/icons/routeway-start.svg";
import TrashIcon from "assets/icons/trash.svg";
import UGVIcon from "assets/icons/ugv.svg";

import styles from "./map-ugv-details.module.scss";

const validationSchema = Yup.object().shape({
  enterLtd: Yup.string().required("Project name is required"),
  enterLng: Yup.string().required("Project name is required"),
  exitLtd: Yup.string().required("Project name is required"),
  exitLng: Yup.string().required("Project name is required")
});

interface MapUGVDetailsInterface {
  onHide: () => void;
  style: any;
}
const MapUGVDetails = ({ style, onHide }: MapUGVDetailsInterface) => {
  const formRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const { id: projectId } = useParams();

  const { vehicle } = useSelector<
    RootStateInterface,
    MissionsManagerStateInterface
  >((state) => state.missionsManager);

  const initialValues = {
    enterLtd: "",
    enterLng: "",
    exitLtd: "",
    exitLng: ""
  };

  const onSubmit = useCallback((val) => {
    const vehiclePositionData = {
      in_point: {
        coordinates: [val.enterLtd, val.enterLng],
        type: "Point"
      },
      out_point: {
        coordinates: [val.exitLtd, val.exitLng],
        type: "Point"
      }
    };
    onAddVehicle(vehiclePositionData);
  }, []);

  const formik = useFormik({
    validateOnChange: true,
    initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });

  const onSetPointPosition = useCallback(
    (pointType: VehiclePointType) => () => {
      dispatch(
        features.missionsManager.actions.awaitVehiclePosition(pointType)
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (vehicle.in_point.coordinates) {
      formik.setFieldValue("enterLtd", vehicle.in_point.coordinates[0] ?? "");
      formik.setFieldValue("enterLng", vehicle.in_point.coordinates[1] ?? "");
    }
    if (vehicle.out_point.coordinates) {
      formik.setFieldValue("exitLtd", vehicle.out_point.coordinates[0] ?? "");
      formik.setFieldValue("exitLng", vehicle.out_point.coordinates[1] ?? "");
    }
  }, [vehicle]);

  const onDeleteVehicle = useCallback(() => {
    dispatch(
      features.missionsManager.actions.deleteVehicleRequest({
        params: {
          projectId,
          vehicleIndex: vehicle.vehicleIndex
        }
      })
    );
  }, [dispatch, projectId, vehicle.vehicleIndex]);

  const onAddVehicle = useCallback(
    (val) => {
      dispatch(
        features.missionsManager.actions.createVehicleRequest({
          params: { projectId },
          val
        })
      );
      onHide();
    },
    [dispatch, onHide, projectId]
  );

  const onUpdateVehicle = useCallback(
    (val) => () => {
      const vehiclePositionData = {
        in_point: {
          coordinates: [val.enterLtd, val.enterLng],
          type: "Point"
        },
        out_point: {
          coordinates: [val.exitLtd, val.exitLng],
          type: "Point"
        }
      };

      dispatch(
        features.missionsManager.actions.updateVehicleRequest({
          params: { projectId },
          val: vehiclePositionData
        })
      );
      onHide();
    },
    [dispatch, onHide, projectId]
  );
  return createPortal(
    <div ref={formRef} style={style} className={styles["wrapper"]}>
      <div className={styles["wrapper__head"]}>
        <div className={styles["head__icon"]}>
          <img src={UGVIcon} alt="ugv-details-icon" />
        </div>
        {vehicle.vehicleIndex !== undefined && (
          <div className={styles["head__title"]}>
            {`Vehicle #${vehicle.vehicleIndex + 1}`}
          </div>
        )}

        <div className={styles["head__close"]} onClick={onHide}>
          <img src={CloseIcon} alt="close-details-icon" />
        </div>
      </div>
      <FormikProvider value={formik}>
        <Form className={styles["wrapper__content"]}>
          {/* <div className={styles["wrapper__parameters"]}>
            <div className={styles["parameter"]}>
              <FormField name={"speed"} label="Speed" dimension="km/h" />
            </div>
            <div className={styles["parameter"]}>
              <FormField name={"capture"} label="Capture" dimension="mm" />
            </div>
          </div> */}

          <div className={styles["point"]}>
            <label className={styles["point__title"]}>Entry point</label>
            <div className={styles["point__form"]}>
              <CommonButton
                icon={RoutewayStartIcon}
                onClick={onSetPointPosition(VehiclePointType.ENTER)}
              >
                Entry Point
              </CommonButton>
              <FormField name={"enterLtd"} />
              <FormField name={"enterLng"} />
            </div>
          </div>
          <div className={styles["point"]}>
            <label className={styles["point__title"]}>Exit Point</label>
            <div className={styles["point__form"]}>
              <CommonButton
                icon={RoutewayFinishIcon}
                onClick={onSetPointPosition(VehiclePointType.EXIT)}
              >
                Exit Point
              </CommonButton>
              <FormField name={"exitLtd"} />
              <FormField name={"exitLng"} />
            </div>
          </div>
          {vehicle.id ? (
            <div className={styles["actions"]}>
              <CommonButton onClick={onDeleteVehicle} icon={TrashIcon}>
                Delete
              </CommonButton>
              <PrimaryButton onClick={onUpdateVehicle(formik.values)}>
                Accept
              </PrimaryButton>
            </div>
          ) : (
            <PrimaryButton type="submit">Save</PrimaryButton>
          )}
        </Form>
      </FormikProvider>
    </div>,
    document.body
  );
};

export default MapUGVDetails;
